import React, { memo } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import type { DailyShiftSlotGrouped, TimeSlot } from '../../../../domain/dailyShiftSlot';
import { DailyShiftSlotPolicy } from '../../../../domain/dailyShiftSlot';
import TimeSlotTable from './TimeSlotTable';
import { ModalContent } from '../MonthlyShiftSlotSetup.styles';

interface DateDetailModalProps {
  open: boolean;
  date: DailyShiftSlotGrouped | null;
  editedSlots: Map<string, Partial<TimeSlot>>;
  onSlotChange: (slotId: string, field: 'recruitmentSlots' | 'urgentSlots', value: number) => void;
  onClose: () => void;
  onSave: () => void;
  onOpenCopyDialog: () => void;
  hasUnsavedChanges: boolean;
  isLoading: boolean;
}

/**
 * 日付詳細設定モーダルコンポーネント
 *
 * - タイトル: 「YYYY年MM月DD日の詳細設定」
 * - 説明文・注意書き表示
 * - TimeSlotTable統合
 * - 更新ボタン
 * - 他の日付へのコピー機能への導線
 */
const DateDetailModal: React.FC<DateDetailModalProps> = memo(({
  open,
  date,
  editedSlots,
  onSlotChange,
  onClose,
  onSave,
  onOpenCopyDialog,
  hasUnsavedChanges,
  isLoading,
}) => {
  if (!date) {
    return null;
  }

  const [year, month, day] = date.date.split('-').map(Number);
  const isEditable = DailyShiftSlotPolicy.isEditable(date.status);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent sx={{ width: 660, minWidth: 0, p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {year}年{month}月{day}日の詳細設定
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            sx={{ minWidth: 'auto' }}
          >
            <CloseIcon />
          </Button>
        </Box>

        {date.isHoliday && date.holidayName && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {date.holidayName}（祝日）
          </Alert>
        )}

        {!isEditable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            確定済みのため編集できません
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          各時間帯の募集医師枠数と急募医師枠数を設定してください。
          募集済み枠数・診療予約枠(合計)・確定医師枠数・不足医師枠数は自動計算されます。
        </Typography>

        <TimeSlotTable
          slots={date.slots}
          isEditable={isEditable}
          editedSlots={editedSlots}
          onSlotChange={onSlotChange}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={onOpenCopyDialog}
              disabled={hasUnsavedChanges || isLoading}
            >
              他の日付にコピー
            </Button>
            {hasUnsavedChanges && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                未保存の変更があります。保存後にコピーできます。
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
            >
              閉じる
            </Button>
            {isEditable && (
              <Button
                variant="contained"
                onClick={onSave}
                disabled={!hasUnsavedChanges || isLoading}
              >
                {isLoading ? '保存中...' : '保存'}
              </Button>
            )}
          </Box>
        </Box>
      </ModalContent>
    </Modal>
  );
});

DateDetailModal.displayName = 'DateDetailModal';

export default DateDetailModal;
